import { Injectable } from '@angular/core';
import { ChildrenOutletContexts } from '@angular/router';
import { from, isObservable, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { IdleService } from '../service/idle.service';
import { DeactivationGuarded } from './can-deactivate-guard.service';

@Injectable({
  providedIn: 'root'
})
export class PendingChangesService
{
  constructor(
    private contexts: ChildrenOutletContexts,
    private idleService: IdleService)
  { }

  /**
   * Runs the action after the active route confirms pending changes may be discarded.
   */
  public runWhenAllowed(action: () => void): void
  {
    const result = this.confirmProceed();

    if (result === true)
    {
      action();
      return;
    }

    if (result === false)
    {
      return;
    }

    const confirmation = isObservable(result) ? result : from(result);
    confirmation.pipe(take(1)).subscribe({
      next: (allowed) =>
      {
        if (allowed)
        {
          action();
        }
      }
    });
  }

  /**
   * Checks whether the active routed component allows leaving without saving.
   */
  public confirmProceed(): boolean | Observable<boolean> | Promise<boolean>
  {
    if (this.idleService.timedOut)
    {
      return true;
    }

    const component = this.getActiveDeactivationGuarded();
    if (!component?.canDeactivate)
    {
      return true;
    }

    return component.canDeactivate();
  }

  private getActiveDeactivationGuarded(): DeactivationGuarded | null
  {
    const context = this.contexts.getContext('primary');
    const activeComponent = context?.outlet?.component;

    if (activeComponent && typeof (activeComponent as DeactivationGuarded).canDeactivate === 'function')
    {
      return activeComponent as DeactivationGuarded;
    }

    return null;
  }
}
